import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { TypeBadge } from "../badges";
import { Eye } from "@phosphor-icons/react";

/**
 * Player-eyes preview — shows the challenge as the end user will see it,
 * stripped of internal metadata (quality score, validation, detected signals, etc.).
 */
export default function PlayerPreview({ challenge, open, onOpenChange }) {
    const safeHtml = useMemo(
        () =>
            DOMPurify.sanitize(challenge?.descriptionHtml || "", {
                ALLOWED_TAGS: ["p", "strong", "em", "ul", "ol", "li", "br", "b", "i", "span"],
                ALLOWED_ATTR: [],
            }),
        [challenge?.descriptionHtml],
    );
    if (!challenge) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl bg-[#0a0a0a] border-2 border-fa-yellow text-white rounded-none p-0 max-h-[92vh] overflow-y-auto"
                data-testid="player-preview-dialog"
            >
                <DialogHeader className="border-b border-white/10 p-5 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
                    <div className="flex items-center gap-2 mb-2">
                        <Eye size={14} weight="fill" className="text-fa-yellow" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                            Player preview
                        </span>
                        <TypeBadge type={challenge.challengeType} />
                    </div>
                    {challenge.imageBase64 && (
                        <img
                            src={`data:image/png;base64,${challenge.imageBase64}`}
                            alt="Challenge"
                            className="w-full border border-white/10 mb-3"
                        />
                    )}
                    <DialogTitle className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight leading-none">
                        {challenge.title}
                    </DialogTitle>
                    <DialogDescription className="text-white/70 mt-2">
                        {challenge.shortDescription}
                    </DialogDescription>
                </DialogHeader>

                <div className="p-5 space-y-4">
                    {safeHtml && (
                        <div
                            className="challenge-html text-white/80"
                            dangerouslySetInnerHTML={{ __html: safeHtml }}
                        />
                    )}

                    {challenge.questions?.length > 0 && (
                        <div className="space-y-3">
                            {challenge.questions.map((q, qi) => (
                                <div key={q.id} className="border border-white/10 bg-black/60 p-3">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-2">
                                        Q{qi + 1} · {q.type.replace("_", " ")}
                                    </div>
                                    <div className="font-display font-bold uppercase mb-3">
                                        {q.text}
                                    </div>
                                    <div className="space-y-2">
                                        {(q.choices || []).map((c) => (
                                            <label
                                                key={`${q.id}-${c.text}`}
                                                className="flex items-start gap-3 bg-black border border-white/10 hover:border-fa-yellow/50 p-2 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type={q.type === "multi_select" ? "checkbox" : "radio"}
                                                    name={q.id}
                                                    disabled
                                                    className="mt-0.5 accent-fa-yellow"
                                                />
                                                <div>
                                                    <div className="font-display font-bold uppercase text-sm">
                                                        {c.text}
                                                    </div>
                                                    {c.description && (
                                                        <div className="text-xs text-white/70 leading-snug mt-0.5">
                                                            {c.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {challenge.rules?.length > 0 && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                                Rules
                            </div>
                            <ul className="space-y-1 text-sm text-white/80 pl-4">
                                {challenge.rules.map((r) => (
                                    <li key={r} className="list-disc">
                                        {r}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {challenge.scoring?.length > 0 && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                                Scoring
                            </div>
                            <ul className="space-y-1 text-sm text-white/80 pl-4">
                                {challenge.scoring.map((s) => (
                                    <li key={s} className="list-disc">
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {challenge.hint && (
                        <div className="border-l-2 border-fa-cyan bg-fa-cyan/5 pl-3 py-2">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cyan mb-1">
                                Hint
                            </div>
                            <div className="text-sm text-white/80 italic">{challenge.hint}</div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
